import React from "react";
import { Alert, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle, faHourglassHalf } from "@fortawesome/free-solid-svg-icons";

const PreliminaryReconciliationAssessment = ({ siteData, sources }) => {
  // calculate total emissions from all sources
  const totalSourceEmissions = sources
    .filter((source) => source.sourceCategoryId && source.emissions)
    .reduce((sum, source) => sum + parseFloat(source.emissions || 0), 0);

  // default status is DATA_PENDING
  let reconciliationStatus = "DATA_PENDING";
  let statusIcon = faHourglassHalf;
  let statusColor = "text-secondary";
  let statusText = "DATA PENDING";

  // only perform calculation if reconciliation status is READY
  if (siteData.reconciliationStatus === "READY") {
    const siteEmissions = parseFloat(siteData.emissions || 0);
    const siteLevelUncertainty = parseFloat(siteData.siteLevelUncertainty || 0);
    const sourceLevelUncertainty = parseFloat(siteData.sourceLevelUncertainty || 0);

    // calculate the site and source ranges.
    const site_start = (1 - siteLevelUncertainty) * siteEmissions;
    const site_end = (1 + siteLevelUncertainty) * siteEmissions;

    const source_start = (1 - sourceLevelUncertainty) * totalSourceEmissions;
    const source_end = (1 + sourceLevelUncertainty) * totalSourceEmissions;

    if (site_start <= source_end && source_start <= site_end) {
      reconciliationStatus = "RECONCILED";
      statusIcon = faCheckCircle;
      statusColor = "text-success";
      statusText = "RECONCILED";
    } else {
      reconciliationStatus = "NOT_RECONCILED";
      statusIcon = faTimesCircle;
      statusColor = "text-danger";
      statusText = "NOT RECONCILED";
    }
  }

  return (
    <div className="reconciliation-section mb-5 p-4 bg-light border rounded">
      <h5 className="text-white p-2" style={{ backgroundColor: "#243544" }}>
        Preliminary Reconciliation Assessment
      </h5>

      <Card className="p-4 border">
        <div className="d-flex flex-column align-items-center justify-content-center">
          <FontAwesomeIcon icon={statusIcon} className={`${statusColor} mb-3`} style={{ fontSize: "3rem" }} />
          <h4 className="text-center fw-bold">
            PRELIMINARY STATUS: <span className={statusColor}>{statusText}</span>
          </h4>

          {reconciliationStatus !== "DATA_PENDING" && (
            <div className="mt-3 text-center">
              <p className="mb-2">
                <strong>Site Emissions:</strong> {siteData.emissions || 0} {siteData.emissionsUnit || "kgCH4"}
                {siteData.siteLevelUncertainty && ` (±${(siteData.siteLevelUncertainty * 100).toFixed(1)}%)`}
              </p>
              <p className="mb-2">
                <strong>Source Emissions Total:</strong> {totalSourceEmissions.toFixed(3)} {sources[0]?.emissionsUnit || "kgCH4"}
                {siteData.sourceLevelUncertainty && ` (±${(siteData.sourceLevelUncertainty * 100).toFixed(1)}%)`}
              </p>

              {reconciliationStatus === "RECONCILED" && (
                <Alert variant="success" className="mt-3">
                  <Alert.Heading>Reconciliation Criteria Met</Alert.Heading>
                  <p>
                    The site level measurements and source level measurements overlap within their uncertainty ranges, indicating
                    preliminary reconciliation.
                  </p>
                </Alert>
              )}

              {reconciliationStatus === "NOT_RECONCILED" && (
                <Alert variant="warning" className="mt-3">
                  <Alert.Heading>Reconciliation Criteria Not Met</Alert.Heading>
                  <p>
                    The site level measurements and source level measurements do not overlap within their uncertainty ranges, indicating a
                    reconciliation issue that should be addressed.
                  </p>
                </Alert>
              )}
            </div>
          )}

          {reconciliationStatus === "DATA_PENDING" && (
            <Alert variant="secondary" className="mt-3 w-75">
              <Alert.Heading>Reconciliation Status Pending</Alert.Heading>
              <p>Set the Reconciliation Ready Status to "READY" to perform a preliminary reconciliation assessment.</p>
            </Alert>
          )}
        </div>
      </Card>
    </div>
  );
};

export { PreliminaryReconciliationAssessment };
